import type { FunctionComponent, ReactNode, SVGProps } from "react";
import { Focusable, Tooltip as AriaTooltip, TooltipProps as AriaTooltipProps, TooltipTrigger } from "react-aria-components";
import { cn } from "@/utils/helpers";
import { useCoShellPortal } from "../../CoShellPortalContext";

/*
 * Tooltips (CreativeOS Library.lib.pen, Components / Overlays, style B). A
 * tooltip names what a control does; the action tooltip adds its key. The
 * label tooltip stays solid for its tiny text (the inverse ink plane, radius
 * 8); the action tooltip is floating chrome (tier 1), radius 8.
 */
export interface KitTooltipProps extends Omit<AriaTooltipProps, "children"> {
  children: ReactNode;
  /** label: the solid inverse tooltip. action: the glass one with an icon. */
  variant?: "label" | "action";
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  /** The key that does the same (N, C), printed in a key cap. */
  shortcut?: string;
}

export function KitTooltip({ children, variant = "label", icon: Glyph, shortcut, className, offset = 8, ...props }: KitTooltipProps) {
  const portalContainer = useCoShellPortal();
  const action = variant === "action";
  return (
    <AriaTooltip
      offset={offset}
      UNSTABLE_portalContainer={portalContainer ?? undefined}
      {...props}
      className={(renderProps) =>
        cn(
          "z-[var(--z-index-tooltip)] inline-flex items-center gap-2 rounded-tip text-[12px] outline-none",
          action
            ? "co-glass-chrome h-7 px-2.5 font-semibold text-co-text"
            : "bg-co-inverse px-2 py-[5px] font-medium text-co-on-inverse shadow-[0_4px_12px_var(--co-panel-shadow)]",
          renderProps.isEntering &&
            "animate-in fade-in placement-bottom:slide-in-from-top-1 placement-top:rise-in-2",
          renderProps.isExiting && "animate-out fade-out",
          typeof className === "string" ? className : undefined,
        )
      }
    >
      {Glyph ? <Glyph aria-hidden className="size-3.5 shrink-0" /> : null}
      {children}
      {shortcut ? (
        <kbd
          className={cn(
            "rounded-[4px] border px-[5px] py-px font-mono text-[10px] leading-[1.4] font-normal opacity-60",
            action ? "border-co-line2" : "border-co-on-inverse",
          )}
        >
          {shortcut}
        </kbd>
      ) : null}
    </AriaTooltip>
  );
}

/** KitTooltipTrigger, then the trigger wrapped in KitFocusable (kit buttons are plain buttons), then KitTooltip. */
export { TooltipTrigger as KitTooltipTrigger, Focusable as KitFocusable };
